import { getPreferencesSnapshot } from '../features/preferences/preferencesStore';

const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** Renders a date in an explicit style, so settings can preview each choice with the exact formatter the app uses. */
export function formatDateInStyle(date: Date, style: string): string {
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  switch (style) {
    case 'dMonY':
      return `${date.getDate()} ${MONTH_ABBR[date.getMonth()]} ${year}`;
    case 'iso':
      return `${year}-${month}-${day}`;
    default:
      return `${day}-${month}-${year}`;
  }
}

/** The person's chosen date style; the default (dmy) keeps the long-standing DD-MM-YYYY output. */
function formatDateParts(date: Date): string {
  return formatDateInStyle(date, getPreferencesSnapshot().region.dateFormat);
}

/** HH:MM on the 24-hour clock, or h:MM am/pm when the person prefers a 12-hour clock. */
function formatClock(date: Date): string {
  const minutes = String(date.getMinutes()).padStart(2, '0');
  if (getPreferencesSnapshot().messages.clock24h) return `${String(date.getHours()).padStart(2, '0')}:${minutes}`;
  const hours = date.getHours();
  return `${hours % 12 || 12}:${minutes} ${hours < 12 ? 'am' : 'pm'}`;
}

/**
 * Safely create a Date object, returning undefined if invalid
 */
export function safeDate(dateStr: string | Date | undefined | null): Date | undefined {
  if (!dateStr) return undefined;
  
  const date = dateStr instanceof Date ? dateStr : new Date(dateStr);
  
  // Check if date is valid
  if (isNaN(date.getTime())) return undefined;
  
  return date;
}

/**
 * Format date string to DD-MM-YYYY format
 */
export function formatDate(dateStr: string | Date | undefined): string {
  if (!dateStr) return 'N/A';
  
  const date = safeDate(dateStr);
  
  // Check if date is valid
  if (!date) return 'N/A';
  
  return formatDateParts(date);
}

const MS_PER_DAY = 24 * 60 * 60 * 1000;

// Whole calendar days between two local dates. Dividing the gap between two
// local midnights by 24 hours is wrong across a clock change (the day is 23 or
// 25 hours long), which put every count one out for the days after one.
function calendarDaysBetween(from: Date, to: Date): number {
  const start = Date.UTC(from.getFullYear(), from.getMonth(), from.getDate());
  const end = Date.UTC(to.getFullYear(), to.getMonth(), to.getDate());
  return Math.round((end - start) / MS_PER_DAY);
}

/**
 * Calculate days until a date
 */
export function getDaysUntil(dateStr: string | Date | undefined): number | null {
  const date = safeDate(dateStr);
  if (!date) return null;

  return calendarDaysBetween(new Date(), date);
}

/**
 * Calculate days since a date
 */
export function getDaysSince(dateStr: string | Date | undefined): number | null {
  const date = safeDate(dateStr);
  if (!date) return null;

  return calendarDaysBetween(date, new Date());
}

/**
 * Format date as relative time (e.g., "2 days ago", "in 3 days")
 */
export function formatRelativeDate(dateStr: string | Date | undefined): string {
  const days = getDaysUntil(dateStr);
  
  if (days === null) return 'N/A';
  
  if (days === 0) return 'Today';
  if (days === 1) return 'Tomorrow';
  if (days === -1) return 'Yesterday';
  
  if (days > 0) {
    return `In ${days} days`;
  } else {
    return `${Math.abs(days)} days ago`;
  }
}

/**
 * Format date range as DD-MM-YYYY to DD-MM-YYYY
 */
export function formatDateRange(startDate: string | Date | undefined, endDate: string | Date | undefined): string {
  const start = formatDate(startDate);
  const end = formatDate(endDate);
  
  if (start === 'N/A' && end === 'N/A') return 'N/A';
  if (start === 'N/A') return `Until ${end}`;
  if (end === 'N/A') return `From ${start}`;
  
  return `${start} to ${end}`;
}

/**
 * Check if a date is in the past
 */
export function isDatePast(dateStr: string | Date | undefined): boolean {
  const days = getDaysUntil(dateStr);
  return days !== null && days < 0;
}

/**
 * Check if a date is within the next N days
 */
export function isDateWithinDays(dateStr: string | Date | undefined, days: number): boolean {
  const daysUntil = getDaysUntil(dateStr);
  return daysUntil !== null && daysUntil >= 0 && daysUntil <= days;
}

/**
 * Format date and time as DD-MM-YYYY HH:MM
 */
export function formatDateTime(dateStr: string | Date | undefined): string {
  if (!dateStr) return 'N/A';
  
  const date = dateStr instanceof Date ? dateStr : new Date(dateStr);
  
  // Check if date is valid
  if (isNaN(date.getTime())) return 'N/A';
  
  return `${formatDateParts(date)} ${formatClock(date)}`;
}

/**
 * Format time only as HH:MM
 */
export function formatTime(dateStr: string | Date | undefined): string {
  if (!dateStr) return 'N/A';
  
  const date = dateStr instanceof Date ? dateStr : new Date(dateStr);
  
  // Check if date is valid
  if (isNaN(date.getTime())) return 'N/A';
  
  return formatClock(date);
}